import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import theme from '../../../theme';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import NumberFormat from 'react-number-format';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../../services/apiClient';
import { BaseTextField } from '../../textfield/StyledTextField';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SaveIcon from '@mui/icons-material/Save';
import { myStyles, NotificationAlert } from '../../index';
import AlertBox from '../../alert/AlertBox';

// ---- Phone mask adapter (JS)
const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <NumberFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            format="(###) ###-####"
            allowEmptyFormatting
            mask="_"
        />
    );
});

function TenantsDetails({ handleNext, handleBack, owner, setOwner }) {
    const { t } = useTranslation();
    const [changeMade, setChangeMade] = useState(false);
    const [errors, setErrors] = useState({ msg: false });
    const [tenantSucces, setTenantSucces] = useState(false);
    const [errorDuplicateTenantNumbers, setErrorDuplicateTenantNumbers] = useState(false);
    const [deletingIds, setDeletingIds] = useState([]);
    const isDeleting = deletingIds.length > 0;

    const { control, reset, handleSubmit, getValues } = useForm({
        reValidateMode: 'onBlur',
        defaultValues: { tenants: owner?.tenants || [] },
    });

    const { fields: tenantFields, append: appendTenantsRow, remove: removeTenantsRow } = useFieldArray({
        control,
        name: 'tenants',
    });

    const emptyRow = useMemo(
        () => () => ({
            identification: '',
            tenantNumber: '',
            email: '',
            hasUnsubscribe: false,
            tenantEmail: false,
            tenantSMS: false,
            tenantVoice: false,
            referenceFK: owner?.clientReference,
            roadFK: owner?.roadFK,
        }),
        [owner?.clientReference, owner?.roadFK]
    );

    const onFieldsChange = () => {
        setChangeMade(true);
        setErrorDuplicateTenantNumbers(false);
    };

    // Recharger le owner depuis la base de données
    const { refetch: refetchOwner } = useQuery(
        ['fetchOwner', owner?.clientContractNumber, owner?.clientReference],
        async () => {
            return await apiClient.fetchOwner({
                clientContract: owner?.clientContractNumber,
                clientReference: owner?.clientReference
            });
        },
        {
            enabled: false,
            onSuccess: ({ data }) => {
                if (data) {
                    setOwner(data);
                }
            }
        }
    );

    // -------- mutations
    const { mutate: crudTenantsList } = useMutation(
        async (tenants) => {
            setErrors({ msg: false });
            const tenantsList = { tenants, clientReference: owner?.clientReference, roadFK: owner.roadFK };
            // Ne pas mettre à jour le owner ici, laisser le backend gérer
            return await apiClient.crudTenantsList(tenantsList);
        },
        { onSuccess: () => handleNext(), onError: () => setErrors({ msg: true }) }
    );

    const { mutate: crudTenant } = useMutation(
        async ({ tenant, showToast = true }) => {
            setErrors({ msg: false });
            setTenantSucces(false);
            const tenantsList = { tenants: [tenant], clientReference: owner?.clientReference, roadFK: owner.roadFK };
            const result = await apiClient.crudTenantsList(tenantsList);
            return { result, tenant, showToast };
        },
        {
            onSuccess: async (data) => {
                const { tenant, showToast } = data;
                if (showToast) {
                    setTenantSucces(true);
                }
                // Recharger le owner depuis la base de données pour avoir les dernières données
                await refetchOwner();
                
                // Mettre à jour le form avec le tenant sauvegardé (qui devrait avoir un ID maintenant)
                const savedTenant = data?.result?.data?.tenants?.[0] || tenant;
                if (savedTenant?.id) {
                    // Trouver l'index du tenant dans le form et le mettre à jour avec l'ID
                    const currentValues = getValues();
                    const tenantIndex = currentValues.tenants.findIndex((t) => 
                        t.identification === tenant.identification && 
                        t.tenantNumber === tenant.tenantNumber && 
                        t.email === tenant.email &&
                        !t.id
                    );
                    if (tenantIndex !== -1) {
                        // Mettre à jour le form avec l'ID
                        const updatedTenants = [...currentValues.tenants];
                        updatedTenants[tenantIndex] = { ...savedTenant };
                        reset({ tenants: updatedTenants });
                    }
                }
                addLog({
                    action: `Gestion Locataires - Ajout/Modification Locataire - ${owner.clientReference} - ${
                        tenant?.identification || tenant?.tenantNumber || tenant?.email
                    }`,
                    user: 'Propriétaire',
                });
            },
            onError: () => setErrors({ msg: true }),
        }
    );

    const { mutate: addLog } = useMutation(async (log) => apiClient.addLogGestionLocataire(log));

    const { mutate: checkUnsubscribed } = useMutation(
        async (phoneNumber) => {
            // Normaliser le numéro (enlever les caractères spéciaux)
            const normalizedNumber = phoneNumber.replace(/\D/g, '');
            return await apiClient.checkIfUnsubscribed(normalizedNumber);
        },
        {
            onSuccess: async (data, phoneNumber) => {
                const isUnsubscribed = data?.data?.isUnsubscribed;
                if (isUnsubscribed) {
                    // Normaliser le numéro pour la comparaison
                    const normalizedNumber = phoneNumber.replace(/\D/g, '');
                    
                    // Mettre à jour tous les tenants avec ce numéro dans le form
                    const currentValues = getValues();
                    const updatedTenants = currentValues.tenants.map((t) => {
                        // Normaliser le numéro du tenant pour la comparaison
                        const tNormalized = (t.tenantNumber || '').replace(/\D/g, '');
                        if (tNormalized === normalizedNumber) {
                            return { ...t, hasUnsubscribe: true };
                        }
                        return t;
                    });
                    reset({ tenants: updatedTenants });
                }
            },
        }
    );

    const { mutate: deleteTenant } = useMutation(
        async ({ tenantId }) => {
            setErrors({ msg: false });
            return await apiClient.deleteTenantGestion(tenantId);
        },
        {
            onSuccess: (_res, { label }) => {
                addLog({
                    action: `Gestion Locataires - Suppression Locataire - ${owner.clientReference} - ${label || 'N/A'}`,
                    user: 'Propriétaire',
                });
            },
            onError: () => setErrors({ msg: true }),
            onSettled: (_res, _err, { tenantId }) => {
                setDeletingIds((ids) => {
                    const next = ids.filter((id) => id !== tenantId);
                    // Plus aucune suppression en vol : on resynchronise une seule fois
                    // avec la base de données.
                    if (next.length === 0) {
                        refetchOwner();
                    }
                    return next;
                });
            },
        }
    );

    // -------- init form once (or when contract keys change)
    const initKeysRef = useRef({
        clientReference: owner?.clientReference,
        roadFK: owner?.roadFK,
        initialized: false,
    });

    const hasRefetchedRef = useRef(false);
    const lastTenantsRef = useRef(null);

    useEffect(() => {
        const keysChanged =
            initKeysRef.current.clientReference !== owner?.clientReference ||
            initKeysRef.current.roadFK !== owner?.roadFK;

        if (!initKeysRef.current.initialized || keysChanged) {
            const rows = (owner?.tenants ?? [])
                .slice()
                .sort((a, b) => {
                    const A = a?.identification ?? '';
                    const B = b?.identification ?? '';
                    const nA = Number(A);
                    const nB = Number(B);
                    if (!Number.isNaN(nA) && !Number.isNaN(nB)) return nA - nB;
                    return String(A).localeCompare(String(B));
                });

            reset({ tenants: rows.length ? rows : [emptyRow()] });
            setChangeMade(false);
            setErrorDuplicateTenantNumbers(false);

            initKeysRef.current = {
                clientReference: owner?.clientReference,
                roadFK: owner?.roadFK,
                initialized: true,
            };
            
            // Sauvegarder les tenants pour comparaison future
            lastTenantsRef.current = JSON.stringify(rows);
        }
    }, [owner?.clientReference, owner?.roadFK, reset, emptyRow]);

    // Recharger le owner depuis la base de données au montage du composant (une seule fois)
    useEffect(() => {
        if (owner?.clientContractNumber && owner?.clientReference && !hasRefetchedRef.current) {
            refetchOwner();
            hasRefetchedRef.current = true;
        }
    }, [owner?.clientContractNumber, owner?.clientReference, refetchOwner]);

    // Mettre à jour le form quand le owner est rechargé SEULEMENT si les données ont vraiment changé
    useEffect(() => {
        // Pendant un lot de suppressions, ne pas ré-hydrater le form : le owner peut
        // encore contenir des locataires dont le DELETE n'est pas terminé, ce qui
        // ferait « réapparaître » des lignes déjà supprimées à l'écran.
        if (deletingIds.length > 0) return;
        if (owner?.tenants) {
            const rows = owner.tenants
                .slice()
                .sort((a, b) => {
                    const A = a?.identification ?? '';
                    const B = b?.identification ?? '';
                    const nA = Number(A);
                    const nB = Number(B);
                    if (!Number.isNaN(nA) && !Number.isNaN(nB)) return nA - nB;
                    return String(A).localeCompare(String(B));
                });
            
            // Comparer avec la dernière version pour éviter les réinitialisations inutiles
            const currentTenantsStr = JSON.stringify(rows);
            if (currentTenantsStr !== lastTenantsRef.current) {
                // Récupérer les valeurs actuelles du form
                const currentFormValues = getValues();
                const currentFormTenants = currentFormValues.tenants || [];
                
                // Identifier les lignes vides non sauvegardées (sans ID)
                const unsavedEmptyRows = currentFormTenants.filter(t => !t.id && 
                    (!t.identification || t.identification.trim() === '') &&
                    (!t.tenantNumber || t.tenantNumber.trim() === '') &&
                    (!t.email || t.email.trim() === '')
                );
                
                // Fusionner les données du owner avec les lignes vides non sauvegardées
                const mergedTenants = [...rows, ...unsavedEmptyRows];
                
                reset({ tenants: mergedTenants.length ? mergedTenants : [emptyRow()] });
                setChangeMade(false);
                lastTenantsRef.current = currentTenantsStr;
            }
        }
    }, [owner?.tenants, reset, emptyRow, getValues, deletingIds.length]);

    // -------- navigation
    const handleBackBtn = () => {
        if (changeMade) {
            Swal.fire({
                icon: 'warning',
                title: t('gestionLocataires.DataLost'),
                text: t('gestionLocataires.DataLostText'),
                showCancelButton: true,
                confirmButtonColor: theme.palette.primary.dark,
                confirmButtonText: t('app.Yes'),
                cancelButtonText: t('app.No'),
            }).then((result) => {
                if (result.isConfirmed) {
                    reset();
                    handleBack();
                }
            });
        } else {
            handleBack();
        }
    };

    // -------- submit (Next)
    const hasDuplicateTenantNumbers = (tenants) => {
        const tenantNumbers = tenants.map((t) => t.tenantNumber?.trim()).filter(Boolean);
        const unique = new Set(tenantNumbers);
        return unique.size !== tenantNumbers.length;
    };

    const handleOnSubmit = (values) => {
        const filtered = values.tenants.filter(
            (t) =>
                (t.identification && t.identification.trim() !== '') ||
                (t.tenantNumber && t.tenantNumber.trim() !== '') ||
                (t.email && t.email.trim() !== '')
        );

        if (hasDuplicateTenantNumbers(filtered)) {
            setErrorDuplicateTenantNumbers(true);
            Swal.fire({
                icon: 'error',
                title: t('gestionLocataires.DuplicateNumberError'),
                text: t('gestionLocataires.DuplicateNumberErrorMessage'),
            });
            return;
        }
        setErrorDuplicateTenantNumbers(false);
        crudTenantsList(filtered);
    };

    // -------- row actions
    const saveAndAddNewRow = (index) =>
        handleSubmit(async (data) => {
            const currentTenant = data.tenants[index];
            
            // Si le tenant n'est PAS désabonné, on peut le sauvegarder
            if (!currentTenant?.hasUnsubscribe) {
                // Vérifier si le numéro est dans la liste des désabonnés
                if (currentTenant?.tenantNumber) {
                    checkUnsubscribed(currentTenant.tenantNumber);
                }
                
                // Ne pas mettre à jour le owner ici, attendre la réponse du backend
                crudTenant({ tenant: currentTenant, showToast: false });
            }
            
            // Toujours ajouter une nouvelle ligne, même si la ligne actuelle est désabonnée
            appendTenantsRow(emptyRow());
            setChangeMade(false);
        })();

    const saveTenant = (index) =>
        handleSubmit(async (data) => {
            const currentTenant = data.tenants[index];
            // Ne pas sauvegarder si le tenant a unsubscribe
            if (currentTenant?.hasUnsubscribe) {
                return;
            }
            
            // Vérifier si le numéro est dans la liste des désabonnés
            if (currentTenant?.tenantNumber) {
                checkUnsubscribed(currentTenant.tenantNumber);
            }
            
            // Ne pas mettre à jour le owner ici, attendre la réponse du backend
            crudTenant({ tenant: currentTenant, showToast: true });
            setChangeMade(false);
        })();

    const handleDeleteTenant = (index) => {
        const row = getValues(`tenants.${index}`); // lire la ligne cliquée
        const tenantId = row?.id;

        // Toujours enlever la ligne de l'UI immédiatement
        removeTenantsRow(index);

        if (tenantId) {
            const label = row?.identification || row?.tenantNumber || row?.email || 'N/A';
            // Retrait optimiste du owner (par ID) pour ne pas ré-hydrater la ligne
            setOwner((prev) => ({
                ...prev,
                tenants: (prev.tenants ?? []).filter((t) => t.id !== tenantId),
            }));
            setDeletingIds((ids) => (ids.includes(tenantId) ? ids : [...ids, tenantId]));
            deleteTenant({ tenantId, label });
        } else {
            // ligne locale non sauvegardée : retirer seulement du form et, si présent, du owner par index
            setOwner((prev) => ({
                ...prev,
                tenants: (prev.tenants ?? []).filter((_, i) => i !== index),
            }));
        }
    };

    // -------- render
    return (
        <>
            <NotificationAlert
                openNotification={Boolean(errors?.msg)}
                type="error"
                message={t('gestionLocataires.ErrorWhileSaving')}
            />
            <NotificationAlert
                openNotification={tenantSucces}
                type="success"
                message={t('gestionLocataires.TenantSaved')}
            />

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h2" sx={{ mb: 0 }}>
                        {t('gestionLocataires.Step3')}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {t('gestionLocataires.Step3Text')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <AlertBox type="warning" message={t('gestionLocataires.ConsentementLocataire')} />
                </Grid>

                <Grid item xs={12}>
                    {tenantFields.map((field, index) => {
                        const tenantData = getValues(`tenants.${index}`);
                        const isUnsubscribed = tenantData?.hasUnsubscribe === true;
                        
                        return (
                        <Grid container key={field.id} spacing={1} alignItems="center" className="tenant-separator">
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.identification`}
                                    render={({ field }) => (
                                        <BaseTextField
                                            {...field}
                                            fullWidth
                                            variant="outlined"
                                            label={t('client.Identification')}
                                            sx={{ mb: 0 }}
                                            disabled={isUnsubscribed}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                onFieldsChange();
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.tenantNumber`}
                                    render={({ field }) => (
                                        <BaseTextField
                                            {...field}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setErrorDuplicateTenantNumbers(false);
                                            }}
                                            sx={{ mb: 0 }}
                                            label={t('client.Phone')}
                                            fullWidth
                                            id={`tenants.${index}.tenantNumber`}
                                            InputProps={{ inputComponent: NumberFormatCustom }}
                                            variant="outlined"
                                            disabled={isUnsubscribed}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.email`}
                                    rules={{
                                        required: false,
                                        pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                                    }}
                                    render={({ field }) => (
                                        <BaseTextField
                                            {...field}
                                            id={`tenants.${index}.email`}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                onFieldsChange();
                                            }}
                                            fullWidth
                                            variant="outlined"
                                            label={t('client.Email')}
                                            sx={{ mb: 0 }}
                                            disabled={isUnsubscribed}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={2}>
                                {index === tenantFields.length - 1 ? (
                                    <Tooltip title={t('gestionLocataires.AddTenant')}>
                    <span className="add-tenant">
                      <IconButton onClick={() => saveAndAddNewRow(index)} disabled={isDeleting}>
                        <GroupAddIcon sx={myStyles.iconTitle} />
                      </IconButton>
                    </span>
                                    </Tooltip>
                                ) : (
                                    <p />
                                )}

                                <Tooltip title={t('gestionLocataires.SaveTenants')}>
                  <span className="save-tenant">
                    <IconButton
                      onClick={() => saveTenant(index)}
                      disabled={isDeleting}
                      sx={isUnsubscribed ? {
                        '&:hover': { 
                          backgroundColor: 'transparent' 
                        } 
                      } : {}}
                    >
                      <SaveIcon sx={isUnsubscribed ? { 
                        ...myStyles.iconTitle, 
                        color: 'grey',
                        '&:hover': {
                          color: 'grey'
                        }
                      } : myStyles.iconTitle} />
                    </IconButton>
                  </span>
                                </Tooltip>

                                <Tooltip title={t('gestionLocataires.Delete')}>
                                    <span className="delete-tenant">
                                        <IconButton
                                            onClick={() => handleDeleteTenant(index)}
                                            disabled={isDeleting}
                                        >
                                            <DeleteIcon sx={myStyles.iconTitle} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>

                            <Grid item xs={12} style={{ display: 'block' }}>
                                {isUnsubscribed && (
                                    <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Le client s'est retiré volontairement du système. Il ne souhaite ne plus recevoir de notifications.
                                    </Typography>
                                )}
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.tenantVoice`}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={<Checkbox checked={field.value} sx={{ pt: 0, pb: 0 }} {...field} disabled={isUnsubscribed} />}
                                            label="Téléphone"
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.tenantSMS`}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={<Checkbox checked={field.value} sx={{ pt: 0, pb: 0 }} {...field} disabled={isUnsubscribed} />}
                                            label="SMS (Texto)"
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`tenants.${index}.tenantEmail`}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <Controller
                                            control={control}
                                            name={`tenants.${index}.hasUnsubscribe`}
                                            render={({ field: { value: hasUnsubscribe } }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={field.value}
                                                            sx={{ pt: 0, pb: 0 }}
                                                            {...field}
                                                            disabled={hasUnsubscribe}
                                                        />
                                                    }
                                                    label="Email"
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    );
                    })}
                </Grid>
            </Grid>

            {errorDuplicateTenantNumbers && (
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <AlertBox type="error" message={t('gestionLocataires.DuplicateNumberErrorMessage')} />
                    </Grid>
                </Grid>
            )}

            <div className="btn-component">
                <Button
                    className="next-btn"
                    onClick={handleBackBtn}
                    variant="contained"
                    size="medium"
                    type="button"
                    sx={{ mt: 1, mb: 1 }}
                >
                    {t('app.Back')}
                </Button>
                <Button
                    className="next-btn"
                    onClick={handleSubmit(handleOnSubmit)}
                    variant="contained"
                    size="medium"
                    type="submit"
                    disabled={isDeleting}
                    sx={{ mt: 1, mb: 1 }}
                >
                    {t('app.Next')}
                </Button>
            </div>

            <div>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="h5" sx={{ mb: 0 }}>
                            {t('gestionLocataires.Legend')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <GroupAddIcon sx={myStyles.smallIconTitle} />
                        <Typography variant="body1" sx={{ fontSize: '12px', mt: 2 }}>
                            {t('gestionLocataires.AddIcon')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SaveIcon sx={myStyles.smallIconTitle} />
                        <Typography variant="body1" sx={{ fontSize: '12px', mt: 2 }}>
                            {t('gestionLocataires.SaveIcon')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DeleteIcon sx={myStyles.smallIconTitle} />
                        <Typography variant="body1" sx={{ fontSize: '12px', mt: 2 }}>
                            {t('gestionLocataires.DeleteIcon')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error" sx={{ fontWeight: 'bold', textAlign: 'center', mt: 2 }}>
                            Si le locataire s'est retiré volontairement du système.
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '12px', textAlign: 'center', mt: 1 }}>
                            Pour réactiver les notifications, le client doit envoyer START au : 819-300-5619
                        </Typography>
                    </Grid>
                </Grid>
            </div>
        </>
    );
}

export default TenantsDetails;
